/**
 * Stripe.js posts telemetry to r.stripe.com. Ad blockers often block those requests
 * (net::ERR_BLOCKED_BY_CLIENT), which produces noisy console errors that are outside
 * application control. This module reduces that noise without affecting card APIs.
 */
function isStripeRadarUrl(url: string): boolean {
  try {
    return new URL(url, window.location.origin).hostname === "r.stripe.com";
  } catch {
    return url.includes("r.stripe.com");
  }
}

function resolveUrlFromFetchInput(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (input instanceof Request) return input.url;
  return "";
}

function shouldSuppressUnhandledRejection(reason: unknown): boolean {
  const message =
    typeof reason === "object" && reason !== null && "message" in reason
      ? String((reason as { message?: string }).message)
      : String(reason ?? "");
  const stack =
    typeof reason === "object" && reason !== null && "stack" in reason
      ? String((reason as { stack?: string }).stack)
      : "";
  return (
    message.includes("r.stripe.com") ||
    message.includes("stripe.com/b") ||
    stack.includes("r.stripe.com")
  );
}

function install(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("unhandledrejection", (event) => {
    if (shouldSuppressUnhandledRejection(event.reason)) {
      event.preventDefault();
    }
  });

  if (!import.meta.env.DEV) return;

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = resolveUrlFromFetchInput(input);
    if (isStripeRadarUrl(url)) {
      return Promise.resolve(
        new Response("{}", {
          status: 200,
          headers: { "Content-Type": "application/json" },
        }),
      );
    }
    return nativeFetch(input, init);
  };
}

install();
